import React from 'react';
import { CheckCircle2, CreditCard, ExternalLink, KeyRound, Save } from 'lucide-react';

type MercadoPagoForm = {
  mpPublicKey: string;
  mpAccessToken: string;
  hasAccessToken: boolean;
  updatedAt: string;
};

type PaymentsSettingsSectionProps = {
  mercadoPagoForm: MercadoPagoForm;
  paymentFeedback: { type: 'success' | 'error'; message: string } | null;
  paymentSaving: boolean;
  paymentTesting: boolean;
  updateMercadoPagoField: (field: 'mpPublicKey' | 'mpAccessToken', value: string) => void;
  saveMercadoPagoCredentials: (event: React.FormEvent<HTMLFormElement>) => void | Promise<void>;
  testMercadoPagoConnection: () => void | Promise<void>;
};

const PaymentsSettingsSection: React.FC<PaymentsSettingsSectionProps> = ({
  mercadoPagoForm,
  paymentFeedback,
  paymentSaving,
  paymentTesting,
  updateMercadoPagoField,
  saveMercadoPagoCredentials,
  testMercadoPagoConnection,
}) => {
  return (
    <div id="secao-pagamentos" className="card w-full scroll-mt-24 space-y-6 p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Pagamentos</h2>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Conecte a conta do Mercado Pago do proprio restaurante para receber pagamentos online de pedidos delivery.
          </p>
        </div>
        <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
          mercadoPagoForm.hasAccessToken ? 'bg-emerald-500/15 text-emerald-300' : 'bg-yellow-500/15 text-yellow-300'
        }`}>
          {mercadoPagoForm.hasAccessToken ? 'Credenciais salvas' : 'Credenciais pendentes'}
        </span>
      </div>

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1.25fr)_minmax(420px,0.75fr)]">
        <div className="rounded-2xl border border-[#2A2A2A] bg-[#121212] p-5">
          <div className="mb-5 flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/15">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Mercado Pago</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Use a conta Mercado Pago do restaurante. O dinheiro cai direto na conta Mercado Pago do proprio estabelecimento.
              </p>
              {mercadoPagoForm.updatedAt && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Ultima atualizacao: {new Date(mercadoPagoForm.updatedAt).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
          </div>

          <form onSubmit={saveMercadoPagoCredentials} autoComplete="off" className="space-y-5">
            <div className="grid gap-4 xl:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Public Key</label>
                <input
                  name="mercado_pago_public_key"
                  value={mercadoPagoForm.mpPublicKey}
                  onChange={(event) => updateMercadoPagoField('mpPublicKey', event.target.value)}
                  className="input"
                  placeholder="APP_USR- ou TEST-..."
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-white">Access Token</label>
                <input
                  type="password"
                  name="mercado_pago_access_token"
                  value={mercadoPagoForm.mpAccessToken}
                  onChange={(event) => updateMercadoPagoField('mpAccessToken', event.target.value)}
                  className="input"
                  placeholder={mercadoPagoForm.hasAccessToken ? 'Token salvo. Preencha apenas para substituir.' : 'APP_USR-...'}
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                />
                {mercadoPagoForm.hasAccessToken && (
                  <p className="text-xs text-muted-foreground">
                    Um Access Token ja esta salvo com seguranca no backend. Este campo so substitui o atual.
                  </p>
                )}
              </div>
            </div>

            {paymentFeedback && (
              <div
                className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${
                  paymentFeedback.type === 'success'
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                    : 'border-red-500/30 bg-red-500/10 text-red-200'
                }`}
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4" />
                <span>{paymentFeedback.message}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={paymentSaving} className="btn-primary flex items-center gap-2">
                <Save className="h-4 w-4" />
                {paymentSaving ? 'Salvando...' : 'Salvar credenciais'}
              </button>
              <button
                type="button"
                onClick={testMercadoPagoConnection}
                disabled={paymentTesting}
                className="rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white transition hover:border-primary/50"
              >
                {paymentTesting ? 'Testando...' : 'Testar conexao'}
              </button>
            </div>
          </form>
        </div>

        <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-5">
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15">
              <KeyRound className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Como obter suas credenciais</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                O restaurante precisa criar uma aplicacao dentro da propria conta Mercado Pago.
              </p>
            </div>
          </div>
          <ol className="space-y-3 text-sm leading-6 text-muted-foreground">
            <li>1. Acesse o painel de desenvolvedores do Mercado Pago.</li>
            <li>2. Faca login com a conta Mercado Pago do restaurante.</li>
            <li>3. Crie uma aplicacao para o Garcom Digital.</li>
            <li>4. Abra a aba Credenciais.</li>
            <li>5. Entre em Credenciais de Producao.</li>
            <li>6. Copie Public Key e Access Token.</li>
            <li>7. Nao use Client ID nem Client Secret nesta tela.</li>
          </ol>
          <div className="mt-5">
            <a
              href="https://www.mercadopago.com.br/developers/panel"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-[#2A2A2A] px-4 py-3 text-sm font-semibold text-white transition hover:border-primary/50 hover:text-primary"
            >
              <ExternalLink className="h-4 w-4" />
              Abrir painel do Mercado Pago
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsSettingsSection;
